import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, type Settings } from '../api.ts';
import { useFilters, presetRange } from '../filters.tsx';
import DateInput from './DateInput.tsx';
import { Icon } from './Icons.tsx';
import { dateLabel } from '../format.ts';

/** Fetch JSON and re-fetch whenever the URL changes. */
export function useFetch<T>(url: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!url) return;
    const n = ++seq.current;
    setLoading(true);
    try {
      const d = await api.get<T>(url);
      if (n === seq.current) { setData(d); setError(''); }
    } catch (err) {
      if (n === seq.current) setError((err as Error).message);
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, [url]);
  useEffect(() => { void load(); }, [load]);
  return { data, error, loading, reload: load, setData };
}

export function Stat({ label, value, sub, className }: { label: string; value: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className={`value ${className ?? ''}`}>{value}</div>
      {sub != null && <div className="sub">{sub}</div>}
    </div>
  );
}

const PRESETS = [['today', 'Today'], ['week', 'Week'], ['30d', '30D'], ['month', 'Month'], ['ytd', 'YTD'], ['all', 'All']] as const;

/**
 * The shared filters, the same on every page: the period, a "Filters" button that opens the rest, and a chip
 * for each filter that's on (tap its ✕ to drop it). Desktop opens them as a panel under the bar; phones open
 * them in place with a Done button.
 */
/**
 * The shared filters. `showNeeds` offers "Needs notes" (trades still to journal) on the pages that send it, with
 * `needsCount`, how many trades in the range that is.
 */
export function FilterBar({ showResult = true, showNeeds = false, needsCount }: { showResult?: boolean; showNeeds?: boolean; needsCount?: number }) {
  const { filters, setFilters, reset, isDefault: plain } = useFilters();
  const isDefault = plain && !(showNeeds && filters.needs);
  const { data: tags } = useFetch<{ tag: string }[]>('/api/tags');
  const { data: settings } = useFetch<Settings>('/api/settings');
  const { data: accounts } = useFetch<string[]>('/api/accounts');
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const active = filters.preset;

  // The desktop panel closes on Escape or a click outside it (the Filters button toggles it itself).
  useEffect(() => {
    if (!open || mobile) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement;
      if (!panel.current?.contains(el) && !el.closest('.filters-toggle, .date-pop')) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onDown); };
  }, [open, mobile]);

  const day = (d: string) => dateLabel(d, { month: 'short', day: 'numeric' });
  // One chip per filter that's on, each clearing just itself.
  const chips: { key: string; label: string; clear: Partial<typeof filters> }[] = [
    ...(showNeeds && filters.needs ? [{ key: 'needs', label: 'Needs notes', clear: { needs: '' } }] : []),
    ...(!active && (filters.from || filters.to)
      ? [{ key: 'dates', label: `${filters.from ? day(filters.from) : 'Start'} – ${filters.to ? day(filters.to) : 'now'}`, clear: { preset: 'month', ...presetRange('month') } }] : []),
    ...(filters.symbol ? [{ key: 'symbol', label: filters.symbol, clear: { symbol: '' } }] : []),
    ...(filters.side ? [{ key: 'side', label: filters.side === 'long' ? 'Long' : 'Short', clear: { side: '' } }] : []),
    ...(showResult && filters.result ? [{ key: 'result', label: filters.result === 'win' ? 'Winners' : 'Losers', clear: { result: '' } }] : []),
    ...(filters.tag ? [{ key: 'tag', label: `#${filters.tag}`, clear: { tag: '' } }] : []),
    ...(filters.setup ? [{ key: 'setup', label: filters.setup, clear: { setup: '' } }] : []),
    ...(filters.mistake ? [{ key: 'mistake', label: filters.mistake, clear: { mistake: '' } }] : []),
    ...(filters.account ? [{ key: 'account', label: filters.account, clear: { account: '' } }] : []),
  ];

  const presets = (
    <div className="seg presets">
      {PRESETS.map(([k, label]) => (
        <button key={k} className={active === k ? 'on' : ''} onClick={() => setFilters({ preset: k, ...presetRange(k) })}>{label}</button>
      ))}
    </div>
  );
  const controls = (
    <>
      <DateInput label="From" value={filters.from} onChange={(from) => setFilters({ from })} max={filters.to || undefined} placeholder="Start" clearable />
      <DateInput label="To" value={filters.to} onChange={(to) => setFilters({ to })} min={filters.from || undefined} placeholder="Now" clearable />
      {showNeeds && (
        <label className="needs-check" title="Only trades still missing notes, a setup or a rating">
          <input type="checkbox" checked={!!filters.needs} onChange={(e) => setFilters({ needs: e.target.checked ? '1' : '' })} />
          <span className="needs-box" aria-hidden="true"><Icon.check /></span>
          Needs notes
          {needsCount != null && <span className="needs-count">{needsCount}</span>}
        </label>
      )}
      <input className="sym" placeholder="Symbol(s)" aria-label="Symbols" value={filters.symbol} onChange={(e) => setFilters({ symbol: e.target.value.toUpperCase() })} />
      <select aria-label="Side" value={filters.side} onChange={(e) => setFilters({ side: e.target.value })}>
        <option value="">Long & short</option>
        <option value="long">Long</option>
        <option value="short">Short</option>
      </select>
      {showResult && (
        <select aria-label="Result" value={filters.result} onChange={(e) => setFilters({ result: e.target.value })}>
          <option value="">Wins & losses</option>
          <option value="win">Winners</option>
          <option value="loss">Losers</option>
        </select>
      )}
      {!!tags?.length && (
        <select aria-label="Tag" value={filters.tag} onChange={(e) => setFilters({ tag: e.target.value })}>
          <option value="">All tags</option>
          {tags.map((t) => <option key={t.tag} value={t.tag}>{t.tag}</option>)}
        </select>
      )}
      {!!settings?.setups.length && (
        <select aria-label="Setup" value={filters.setup} onChange={(e) => setFilters({ setup: e.target.value })}>
          <option value="">All setups</option>
          {settings.setups.map((x) => <option key={x}>{x}</option>)}
        </select>
      )}
      {!!settings?.mistakes.length && (
        <select aria-label="Mistake" value={filters.mistake} onChange={(e) => setFilters({ mistake: e.target.value })}>
          <option value="">Any mistakes</option>
          {settings.mistakes.map((x) => <option key={x}>{x}</option>)}
        </select>
      )}
      {accounts && accounts.length > 1 && (
        <select aria-label="Account" value={filters.account} onChange={(e) => setFilters({ account: e.target.value })}>
          <option value="">All accounts</option>
          {accounts.map((a) => <option key={a} value={a}>{a || '(no account)'}</option>)}
        </select>
      )}
    </>
  );

  // Phones open the rest under the same row, and the Filters button stays put as "Done" to close it.
  const closing = mobile && open;
  const label = closing ? 'Done' : `Filters${chips.length ? ` · ${chips.length}` : ''}`;
  return (
    <div className="filter-area">
      <div className={mobile ? 'filters-mobile' : 'filters-bar'}>
        {presets}
        <button className={`small filters-toggle ${chips.length && !closing ? 'primary' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open} aria-label={label}>
          {mobile
            // Phones: the presets stretch into whatever the button leaves, so it keeps one width. Its other labels
            // sit invisibly in the same spot and the widest one sets the size.
            ? <span className="toggle-label">
                <span>{label}</span>
                {['Filters · 9', 'Done'].map((l) => <span key={l} aria-hidden="true">{l}</span>)}
              </span>
            : label}
        </button>
        {!mobile && chips.map((c) => <FilterChip key={c.key} label={c.label} onClear={() => setFilters(c.clear)} />)}
        {!mobile && !isDefault && <button className="small ghost" onClick={reset} title="Back to this month with no other filters">Reset</button>}
      </div>
      {closing && (
        <div className="filters" role="group" aria-label="Filters">
          {controls}
          {!isDefault && <button className="small ghost" onClick={reset} title="Back to this month with no other filters">Reset</button>}
        </div>
      )}
      {mobile && !open && chips.length > 0 && (
        <div className="filter-chips">{chips.map((c) => <FilterChip key={c.key} label={c.label} onClear={() => setFilters(c.clear)} />)}</div>
      )}
      {!mobile && open && <div className="filters-panel" ref={panel} role="group" aria-label="Filters">{controls}</div>}
    </div>
  );
}

export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="filter-chip">
      {label}
      <button type="button" onClick={onClear} aria-label={`Remove filter: ${label}`}><Icon.close /></button>
    </span>
  );
}

export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2500);
    return () => clearTimeout(t);
  }, [message, onDone]);
  return <div className="toast" role="status">{message}</div>;
}

/** Whether a media query matches, kept up to date. */
export function useMedia(q: string) {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return m;
}

export const useIsMobile = () => useMedia('(max-width: 820px)');

/** Horizontal +/- bars for P&L by category. */
export function PnlBars({ rows, format }: { rows: { label: string; value: number; sub?: string }[]; format?: (n: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value)));
  const hasNeg = rows.some((r) => r.value < 0);
  const hasPos = rows.some((r) => r.value > 0);
  const zero = hasNeg && hasPos ? 50 : hasNeg ? 100 : 0;
  const scale = hasNeg && hasPos ? 50 : 100;
  return (
    <div>
      {rows.map((r) => {
        const w = (Math.abs(r.value) / max) * scale;
        return (
          <div className="bar-row" key={r.label}>
            <div className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label}</div>
            <div className="bar-track">
              <div className="zero" style={{ left: `${zero}%` }} />
              <div className="bar" style={{
                left: r.value >= 0 ? `${zero}%` : `${zero - w}%`, width: `${w}%`,
                background: r.value >= 0 ? 'var(--pos)' : 'var(--neg)',
              }} />
            </div>
            <div className={`right small num ${r.value > 0 ? 'pos' : r.value < 0 ? 'neg' : ''}`}>
              {format ? format(r.value) : r.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              {r.sub && <div className="muted">{r.sub}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <div className="spinner-wrap" role="status"><span className="spinner" aria-hidden="true" /><span className="muted small">{label}</span></div>;
}

/** Sets the browser tab title, e.g. "AAPL · Edgebook". */
export function useTitle(title?: string | null) {
  useEffect(() => {
    document.title = title ? `${title} · Edgebook` : 'Edgebook';
  }, [title]);
}
